import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { useAuth, authApi, formatApiError } from "../context/AuthContext";
import { PageTitle } from "../components/Shared";
import Seo from "../components/Seo";

const content = {
  it: {
    title: "Area Clienti",
    login: "Accedi",
    register: "Registrati",
    name: "Nome e cognome",
    email: "Email",
    password: "Password",
    passwordMin: "Password (min 6 caratteri)",
    forgot: "Password dimenticata?",
    forgotTitle: "Recupera password",
    forgotText: "Inserisci la tua email: ti invieremo un link per reimpostare la password.",
    forgotSend: "Invia link di recupero",
    forgotSent: "Se l'email \u00e8 registrata riceverai a breve un link di recupero.",
    backToLogin: "Torna al login",
    loginIntro: "Accedi alla tua area personale per scaricare i prodotti acquistati, controllare ordini e abbonamenti e richiedere assistenza.",
    registerIntro: "Crea il tuo account: se hai gi\u00e0 effettuato acquisti con questa email, ritroverai automaticamente i tuoi ordini.",
    wait: "Attendere...",
  },
  en: {
    title: "Customer Area",
    login: "Sign in",
    register: "Sign up",
    name: "Full name",
    email: "Email",
    password: "Password",
    passwordMin: "Password (min 6 characters)",
    forgot: "Forgot password?",
    forgotTitle: "Password recovery",
    forgotText: "Enter your email: we will send you a link to reset your password.",
    forgotSend: "Send recovery link",
    forgotSent: "If the email is registered you will shortly receive a recovery link.",
    backToLogin: "Back to login",
    loginIntro: "Sign in to your personal area to download purchased products, check orders and subscriptions and request support.",
    registerIntro: "Create your account: if you have already purchased with this email, your orders will automatically appear.",
    wait: "Please wait...",
  },
};

const AccountLogin = () => {
  const { lang } = useShop();
  const { lp } = useLocale();
  const { user, login, register } = useAuth();
  const c = content[lang];
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgotDone, setForgotDone] = useState(false);

  if (user && typeof user === "object") {
    const dest = location.state?.from || lp("/account");
    return <Navigate to={dest} replace />;
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate(lp("/account"));
      } else if (mode === "register") {
        await register(form.name, form.email, form.password, lang);
        toast.success(lang === "it" ? "Account creato!" : "Account created!");
        navigate(lp("/account"));
      } else {
        await authApi.post("/auth/forgot-password", { email: form.email, lang });
        setForgotDone(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-login-page">
      <Seo title={c.title} description={c.loginIntro} />
      <PageTitle title={c.title} />
      <div className="idm-container idm-acct-auth">
        {mode !== "forgot" && (
          <div className="idm-acct-tabs" data-testid="account-auth-tabs">
            <button
              className={mode === "login" ? "active" : ""}
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="tab-login"
            >
              {c.login}
            </button>
            <button
              className={mode === "register" ? "active" : ""}
              onClick={() => { setMode("register"); setError(""); }}
              data-testid="tab-register"
            >
              {c.register}
            </button>
          </div>
        )}

        {mode === "forgot" ? (
          <>
            <h3 className="idm-acct-form-title">{c.forgotTitle}</h3>
            {forgotDone ? (
              <p className="idm-acct-ok" data-testid="forgot-sent">{c.forgotSent}</p>
            ) : (
              <>
                <p className="idm-text">{c.forgotText}</p>
                <form onSubmit={submit} noValidate>
                  <div className="idm-field">
                    <label htmlFor="fp-email">{c.email}</label>
                    <input id="fp-email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="forgot-email" />
                  </div>
                  {error && <p className="idm-acct-error" data-testid="auth-error">{error}</p>}
                  <button type="submit" className="idm-btn-yellow" style={{ animation: "none", marginTop: 16 }} disabled={busy} data-testid="forgot-submit">
                    {busy ? <Loader2 size={15} className="idm-spin" /> : null}
                    {busy ? c.wait : c.forgotSend}
                  </button>
                </form>
              </>
            )}
            <button className="idm-acct-link" onClick={() => { setMode("login"); setForgotDone(false); setError(""); }} data-testid="back-to-login">
              {c.backToLogin}
            </button>
          </>
        ) : (
          <>
            <p className="idm-text">{mode === "login" ? c.loginIntro : c.registerIntro}</p>
            <form onSubmit={submit} noValidate>
              {mode === "register" && (
                <div className="idm-field">
                  <label htmlFor="ac-name">{c.name}</label>
                  <input id="ac-name" name="name" value={form.name} onChange={handleChange} required data-testid="register-name" />
                </div>
              )}
              <div className="idm-field">
                <label htmlFor="ac-email">{c.email}</label>
                <input id="ac-email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="auth-email" />
              </div>
              <div className="idm-field">
                <label htmlFor="ac-password">{mode === "register" ? c.passwordMin : c.password}</label>
                <input id="ac-password" name="password" type="password" value={form.password} onChange={handleChange} required data-testid="auth-password" />
              </div>
              {error && <p className="idm-acct-error" data-testid="auth-error">{error}</p>}
              <button type="submit" className="idm-btn-yellow" style={{ animation: "none", marginTop: 16 }} disabled={busy} data-testid="auth-submit">
                {busy ? <Loader2 size={15} className="idm-spin" /> : null}
                {busy ? c.wait : mode === "login" ? c.login : c.register}
              </button>
            </form>
            {mode === "login" && (
              <button className="idm-acct-link" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-link">
                {c.forgot}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountLogin;
